import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import Select from "react-select";
import { toast } from "react-toastify";
import { ImCancelCircle } from "react-icons/im";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import api from "../../config/URL";
import fetchOverAllStudentCompletionForm from "../List/StudentAllCompletedList";
import fetchAllStudentListByCenter from "../List/StudentListByCenter";

// Validation schema
const validationSchema = Yup.object({
  parent: Yup.string().required("Parent is required"),
  student: Yup.string().required("Student is required"),
  course: Yup.string().required("Course is required"),
  packageId: Yup.string().required("Package is required"),
  invoiceDate: Yup.string().required("Invoice Date is required"),
  dueDate: Yup.string().required("Due Date is required"),
  invoicePeriodFrom: Yup.string().required("Invoice Period From is required"),
  invoicePeriodTo: Yup.string().required("Invoice Period To is required"),
  invoiceItems: Yup.array().of(
    Yup.object({
      item: Yup.string().required("Item is required"),
      itemAmount: Yup.number()
        .nullable()
        .required("Item Amount is required")
        .typeError("Item Amount must be a number"),
      taxType: Yup.string().when("item", (item, schema) =>
        typeof item === "string" && item && !item.startsWith("CN-REF")
          ? schema.required("Tax Type is required")
          : schema,
      ),
      gstAmount: Yup.number().when("item", (item, schema) =>
        typeof item === "string" && item && !item.startsWith("CN-REF")
          ? schema
              .nullable()
              .required("GST Amount is required")
              .typeError("GST Amount must be a number")
          : schema.nullable(),
      ),
      totalAmount: Yup.number()
        .nullable()
        .required("Total Amount is required")
        .typeError("Total Amount must be a number"),
    }),
  ),
});

function GenerateInvoiceAdd({ selectedCenter }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const studentID = searchParams.get("studentID");
  const navigate = useNavigate();
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentOverallData, setStudentOverallData] = useState({});
  const [centerPackageOptions, setCenterPackageOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);
  const [scheduleOptions, setScheduleOptions] = useState([]);
  const [taxData, setTaxData] = useState([]);
  const [loadIndicator, setLoadIndicator] = useState(false);
  const userName = localStorage.getItem("userName") || "Unknown User";

  const formik = useFormik({
    initialValues: {
      center: selectedCenter || "",
      parent: "",
      student: studentID || "",
      course: "",
      courseTime: "",
      schedule: "",
      classId: "",
      noOfLessons: "",
      perLessonCost: "",
      remark: "",
      invoiceDate: new Date().toISOString().split("T")[0],
      dueDate: "",
      packageId: "",
      invoicePeriodFrom: "",
      invoicePeriodTo: "",
      receiptAmount: "",
      creditAdviceOffset: 0,
      gst: 0,
      totalAmount: 0,
      invoiceStatus: "PENDING",
      createdBy: userName,
      invoiceItems: [
        {
          item: "",
          itemAmount: 0,
          taxType: "",
          gstAmount: 0,
          totalAmount: 0,
        },
      ],
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoadIndicator(true);
      console.log("Form submitted with values:", values);
      console.log("Invoice items:", values.invoiceItems);
      try {
        const payload = {
          generateInvoice: {
            centerId: selectedCenter,
            parent: values.parent,
            studentId: values.student,
            courseId: values.course,
            courseTime: values.courseTime,
            schedule: values.schedule,
            classId: values.classId,
            invoiceDate: values.invoiceDate,
            dueDate: values.dueDate,
            packageId: values.packageId,
            noOfLessons: values.noOfLessons,
            perLessonCost: values.perLessonCost,
            invoicePeriodFrom: values.invoicePeriodFrom,
            invoicePeriodTo: values.invoicePeriodTo,
            gst: parseFloat(values.gst || 0),
            creditAdviceOffset: parseFloat(values.creditAdviceOffset || 0),
            totalAmount: parseFloat(values.totalAmount || 0),
            remark: values.remark,
            invoiceStatus: "PENDING",
            createdBy: userName,
            receiptAmount: values.receiptAmount,
          },
          invoiceItemsList: values.invoiceItems.map((item) => ({
            item: item.item,
            itemAmount: parseFloat(item.itemAmount || 0),
            taxType: item.taxType,
            gstAmount: parseFloat(item.gstAmount || 0),
            totalAmount: parseFloat(item.totalAmount || 0),
            createdBy: userName,
          })),
        };

        const response = await api.post("/generateInvoice", payload, {
          headers: { "Content-Type": "application/json" },
        });

        if (response.status === 201) {
          toast.success("Invoice Generated successfully");
          navigate(`/invoice/view/${response.data.id}`);
        } else {
          toast.error(response.data.message || "Failed to generate invoice");
        }
      } catch (error) {
        if (error.response?.status === 409) {
          toast.warning(error.response?.data?.message || "Conflict occurred");
        } else {
          toast.error(error.response?.data?.message || "An error occurred");
        }
      } finally {
        setLoadIndicator(false);
      }
    },
    validateOnChange: false,
    validateOnBlur: true,
  });

  // Fetch students
  const fetchStudent = async () => {
    try {
      let student = [];
      const numericCenterId = Number(selectedCenter);

      if (!numericCenterId || numericCenterId === 0) {
        student = await fetchOverAllStudentCompletionForm();
      } else {
        student = await fetchAllStudentListByCenter(numericCenterId);
      }

      if (!Array.isArray(student)) {
        throw new Error("API did not return an array");
      }

      const formattedStudent = student.map((std) => ({
        value: std.id,
        label: std.studentNames,
      }));

      setStudentOptions(formattedStudent);
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error(error.message || "Failed to fetch students");
    }
  };

  // Fetch tax settings
  const fetchTaxData = async () => {
    try {
      const response = await api.get("/getAllTaxSetting");
      setTaxData(response.data);
    } catch (error) {
      toast.error("Error fetching tax data");
      console.error(error);
    }
  };

  // Fetch packages by center ID
  const fetchPackagesByCenter = async () => {
    try {
      if (selectedCenter) {
        const response = await api.get(
          `/getCenterPackageIdsAndNamesByCenterId/${selectedCenter}`,
        );
        const formattedPackages = response.data.map((pkg) => ({
          value: pkg.id,
          label: pkg.packageNames,
          noOfLessons: pkg.noOfLesson,
        }));
        setCenterPackageOptions(formattedPackages);
        setPackageOptions(formattedPackages);
      }
    } catch (error) {
      toast.error("Error fetching center packages");
      console.error(error);
    }
  };

  // Reset form fields
  const resetFormFields = () => {
    formik.setValues({
      ...formik.initialValues,
      center: selectedCenter || "",
      invoiceDate: new Date().toISOString().split("T")[0],
      dueDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
        .toISOString()
        .split("T")[0],
      createdBy: userName,
    });
    setCourseOptions([]);
    setPackageOptions(centerPackageOptions);
    setScheduleOptions([]);
  };

  const fetchInvoiceInfo = async (studentId, courseId, packageId, classId) => {
    try {
      // Base URL
      let url = `/getInvoiceInfoByStudentId/${studentId}?course_id=${courseId}&package_id=${packageId}&class_id=${classId}`;
      const response = await api.get(url);
      const courseTime = response?.data?.courses?.[0]?.courseTime;
      formik.setFieldValue("courseTime", courseTime || "");

      return response.data;
    } catch (error) {
      console.error("Error fetching invoice info:", error);
      toast.error("Error fetching invoice info");
      return null;
    }
  };

  // Fetch class schedules and return classId if available
  const fetchClassSchedules = async (studentId, courseId) => {
    try {
      const response = await api.get(
        `/getStudentClass?studentId=${studentId}&courseId=${courseId}`,
      );
      console.log("Class schedules response:", response.data);
      const formattedSchedules = response.data.map((cls) => ({
        value: cls.classCode,
        label: cls.classCode,
        classId: cls.id, // ← IMPORTANT
      }));
      setScheduleOptions(formattedSchedules);

      if (formattedSchedules.length > 0) {
        formik.setFieldValue("schedule", formattedSchedules[0].value);
        formik.setFieldValue("classId", formattedSchedules[0].classId);
        return formattedSchedules[0].classId; // ← Return proper classId
      }
      return null;
    } catch (error) {
      console.error("Error fetching class schedules:", error);
      toast.error("Error fetching class schedules");
      return null;
    }
  };

  const formatMaterialsLabel = (lessonCount) => {
    if (!lessonCount) return "Materials Fee";
    return `Materials Fee`;
  };

  // ✅ Recalculate GST once tax data is available
  const recalcInvoiceGST = (items) => {
    const updatedItems = items.map((item) => {
      if (!item.taxType || !item.totalAmount) return item;

      const tax = taxData.find((t) => t.id === Number(item.taxType));

      if (!tax) return item;

      const total = parseFloat(item.totalAmount || 0);
      const itemAmount = Math.round(total / (1 + tax.rate / 100));
      const gstAmount = Math.round(total - itemAmount);

      return {
        ...item,
        itemAmount,
        gstAmount,
      };
    });

    const totalGST = updatedItems.reduce(
      (sum, i) => sum + Number(i.gstAmount || 0),
      0,
    );

    const totalAmountSum = updatedItems.reduce(
      (sum, i) => sum + Number(i.totalAmount || 0),
      0,
    );

    formik.setFieldValue("invoiceItems", updatedItems);
    formik.setFieldValue("gst", totalGST.toString());
    formik.setFieldValue("totalAmount", totalAmountSum.toString());
  };

  // Handle student change
  const handleStudentChange = async (studentId) => {
    if (!studentId) {
      resetFormFields();
      return;
    }

    try {
      const courseResponse = await api.get(
        `/getStudentCourse?studentId=${studentId}`,
      );
      // console.log("Student course response:", courseResponse.data);
      const courseData = courseResponse.data;
      // 🔴 IMPORTANT: Handle empty course array
      if (!Array.isArray(courseData) || courseData.length === 0) {
        toast.warning("No course or class assigned for this student");
        resetFormFields();
        return;
      }
      const uniqueCourses = Array.from(
        new Map(
          courseData.map((course) => [
            `${course.id}-${course.courseName}`,
            course,
          ]),
        ).values(),
      );
      const courses = uniqueCourses.map((course) => ({
        value: course.id,
        label: course.courseName,
      }));
      setCourseOptions(courses);

      if (courseData.length > 0) {
        const firstCourse = courseData[0];
        const initialCourseId = firstCourse.id;
        const initialPackageId = firstCourse.packageId;

        formik.setFieldValue("course", initialCourseId);
        formik.setFieldValue("packageId", initialPackageId);

        const classId = await fetchClassSchedules(studentId, initialCourseId);
        const invoiceData = await fetchInvoiceInfo(
          studentId,
          initialCourseId,
          initialPackageId,
          classId,
        );

        if (invoiceData) {
          setStudentOverallData(invoiceData);

          formik.setFieldValue("parent", invoiceData.parentName || "");
          formik.setFieldValue("remark", invoiceData.remarks || " ");
          formik.setFieldValue(
            "creditAdviceOffset",
            parseFloat(invoiceData.creditAdviseOffset || 0),
          );

          const selectedCourse = invoiceData.courses[0] || {};
          formik.setFieldValue("noOfLessons", selectedCourse.noOfLessons || "");
          formik.setFieldValue(
            "perLessonCost",
            selectedCourse.perLessonCost || "",
          );
          formik.setFieldValue(
            "invoicePeriodFrom",
            selectedCourse.invoicePeriodFrom || "",
          );
          formik.setFieldValue(
            "invoicePeriodTo",
            selectedCourse.invoicePeriodTo || "",
          );
          formik.setFieldValue("dueDate", selectedCourse.dueDate || "");
          const invoiceItems = [];
          const hasRegistrationFee =
            parseFloat(selectedCourse.registerFees || 0) > 0;
          const hasDepositFee = parseFloat(selectedCourse.depositFee || 0) > 0;

          if (hasRegistrationFee) {
            invoiceItems.push({
              item: "Registration Fee",
              itemAmount: 0,
              taxType: selectedCourse.registerTaxId
                ? selectedCourse.registerTaxId.toString()
                : "",
              gstAmount: 0,
              totalAmount: parseFloat(selectedCourse.registerFees || 0),
            });
          }

          invoiceItems.push({
            item: selectedCourse.courseFeeName || "Course Fee",
            itemAmount: 0,
            taxType: selectedCourse.courseTaxId
              ? selectedCourse.courseTaxId.toString()
              : "",
            gstAmount: 0,
            totalAmount: parseFloat(selectedCourse.courseFee || 0),
          });

          if (hasDepositFee) {
            invoiceItems.push({
              item: "Deposit Fee",
              itemAmount: 0,
              taxType: selectedCourse.depositTaxId
                ? selectedCourse.depositTaxId.toString()
                : "",
              gstAmount: 0,
              totalAmount: parseFloat(selectedCourse.depositFee || 0),
            });
          }

          // --- MATERIALS FEE ROW (WITH GST CALCULATION) ---
          const hasMaterialsFee =
            parseFloat(selectedCourse.materialsFee || 0) > 0;

          if (hasMaterialsFee) {
            const lessons =
              selectedCourse.noOfLessons || formik.values.noOfLessons;
            let materialTotal = parseFloat(selectedCourse.materialsFee || 0);
            let materialItemAmount = materialTotal;
            let materialGST = 0;

            if (selectedCourse.materialsTaxId) {
              const tax = taxData.find(
                (t) => t.id === Number(selectedCourse.materialsTaxId),
              );

              if (tax) {
                materialItemAmount = Math.round(
                  materialTotal / (1 + tax.rate / 100),
                );
                materialGST = Math.round(materialTotal - materialItemAmount);
              }
            }
            invoiceItems.push({
              item: formatMaterialsLabel(lessons),
              itemAmount: materialItemAmount, // EX-GST
              taxType: selectedCourse.materialsTaxId
                ? selectedCourse.materialsTaxId.toString()
                : "",
              gstAmount: materialGST,
              totalAmount: materialTotal, // INC-GST
            });
          }

          // Push Credit Notes as Negative Line Items
          if (invoiceData.creditNotes && invoiceData.creditNotes.length > 0) {
            invoiceData.creditNotes.forEach((creditNote) => {
              const amount = parseFloat(creditNote.balanceAmount || 0);

              invoiceItems.push({
                item: `CN-REF ${creditNote.creditNote} - ${creditNote.name}`,
                itemAmount: -amount, // negative ex-GST
                taxType: "", // no tax
                gstAmount: 0, // no GST
                totalAmount: -amount, // negative total
              });
            });
          }

          invoiceItems.forEach((item, index) => {
            if (item.taxType && parseFloat(item.totalAmount)) {
              const tax = taxData.find((t) => t.id === parseInt(item.taxType));
              const totalAmount = parseFloat(item.totalAmount || 0);
              if (tax && totalAmount !== 0) {
                const itemAmount = Math.round(
                  totalAmount / (1 + tax.rate / 100),
                );
                const gstAmount = Math.round(totalAmount - itemAmount);
                invoiceItems[index].itemAmount = itemAmount;
                invoiceItems[index].gstAmount = gstAmount;
              } else {
                invoiceItems[index].itemAmount = totalAmount;
                invoiceItems[index].gstAmount = 0;
              }
            } else {
              invoiceItems[index].itemAmount = item.totalAmount || 0;
              invoiceItems[index].gstAmount = 0;
            }
          });

          formik.setFieldValue("invoiceItems", invoiceItems);
          console.log("Updated invoiceItems:", invoiceItems);

          const totalGST = invoiceItems.reduce(
            (sum, item) => sum + parseFloat(item.gstAmount || 0),
            0,
          );
          const totalAmountSum = invoiceItems.reduce(
            (sum, item) => sum + parseFloat(item.totalAmount || 0),
            0,
          );

          formik.setFieldValue("gst", totalGST.toString());
          formik.setFieldValue("totalAmount", totalAmountSum.toString());
        } else {
          resetFormFields();
        }
      } else {
        resetFormFields();
      }
    } catch (error) {
      console.error("Error fetching student data:", error);
      toast.error("Error fetching student data");
      resetFormFields();
    }
  };

  const applyInvoiceDataToForm = (invoiceData) => {
    if (!invoiceData || !invoiceData.courses?.length) return;

    const selectedCourse = invoiceData.courses[0];

    // -------- BASIC FIELDS --------
    formik.setFieldValue("parent", invoiceData.parentName || "");
    formik.setFieldValue("remark", invoiceData.remarks || "");
    formik.setFieldValue(
      "creditAdviceOffset",
      Number(invoiceData.creditAdviseOffset || 0),
    );
    formik.setFieldValue("noOfLessons", selectedCourse.noOfLessons || "");
    formik.setFieldValue("perLessonCost", selectedCourse.perLessonCost || "");
    formik.setFieldValue(
      "invoicePeriodFrom",
      selectedCourse.invoicePeriodFrom || "",
    );
    formik.setFieldValue(
      "invoicePeriodTo",
      selectedCourse.invoicePeriodTo || "",
    );
    formik.setFieldValue("dueDate", selectedCourse.dueDate || "");

    // -------- BUILD INVOICE ITEMS FROM SCRATCH --------
    const invoiceItems = [];

    // Registration Fee
    if (Number(selectedCourse.registerFees) > 0) {
      invoiceItems.push({
        item: "Registration Fee",
        itemAmount: 0,
        taxType: selectedCourse.registerTaxId?.toString() || "",
        gstAmount: 0,
        totalAmount: Number(selectedCourse.registerFees),
      });
    }

    // Course Fee
    invoiceItems.push({
      item: selectedCourse.courseFeeName || "Course Fee",
      itemAmount: 0,
      taxType: selectedCourse.courseTaxId?.toString() || "",
      gstAmount: 0,
      totalAmount: Number(selectedCourse.courseFee || 0),
    });

    // Deposit Fee
    if (Number(selectedCourse.depositFee) > 0) {
      invoiceItems.push({
        item: "Deposit Fee",
        itemAmount: 0,
        taxType: selectedCourse.depositTaxId?.toString() || "",
        gstAmount: 0,
        totalAmount: Number(selectedCourse.depositFee),
      });
    }

    // Materials Fee
    if (Number(selectedCourse.materialsFee) > 0) {
      let total = Number(selectedCourse.materialsFee);
      let itemAmount = total;
      let gstAmount = 0;

      const tax = taxData.find(
        (t) => t.id === Number(selectedCourse.materialsTaxId),
      );
      if (tax) {
        itemAmount = Math.round(total / (1 + tax.rate / 100));
        gstAmount = Math.round(total - itemAmount);
      }

      invoiceItems.push({
        item: formatMaterialsLabel(selectedCourse.noOfLessons),
        itemAmount,
        taxType: selectedCourse.materialsTaxId?.toString() || "",
        gstAmount,
        totalAmount: total,
      });
    }

    // -------- FINAL GST CALC (REUSE YOUR LOGIC) --------
    invoiceItems.forEach((item, i) => {
      if (item.taxType && item.totalAmount) {
        const tax = taxData.find((t) => t.id === Number(item.taxType));
        if (tax) {
          const base = Math.round(item.totalAmount / (1 + tax.rate / 100));
          invoiceItems[i].itemAmount = base;
          invoiceItems[i].gstAmount = Math.round(item.totalAmount - base);
        }
      }
    });

    const totalGST = invoiceItems.reduce(
      (s, i) => s + Number(i.gstAmount || 0),
      0,
    );
    const totalAmount = invoiceItems.reduce(
      (s, i) => s + Number(i.totalAmount || 0),
      0,
    );

    formik.setFieldValue("invoiceItems", invoiceItems);
    formik.setFieldValue("gst", totalGST.toString());
    formik.setFieldValue("totalAmount", totalAmount.toString());
  };

  // Handle course change
  const handleCourseChange = async (courseId) => {
    if (!courseId || !formik.values.student) return;

    const selectedCourseFromData = studentOverallData?.courses?.find(
      (course) => course.courseId === courseId,
    );
    const selectedPackageId =
      selectedCourseFromData?.packageId ||
      formik.values.packageId ||
      packageOptions[0]?.value ||
      "";
    formik.setFieldValue("packageId", selectedPackageId);

    const classId = await fetchClassSchedules(formik.values.student, courseId);
    const invoiceData = await fetchInvoiceInfo(
      formik.values.student,
      courseId,
      selectedPackageId,
      classId,
    );

    if (invoiceData) {
      const selectedCourse = invoiceData.courses[0] || {};
      formik.setFieldValue("noOfLessons", selectedCourse.noOfLessons || "");
      formik.setFieldValue("perLessonCost", selectedCourse.perLessonCost || "");
      formik.setFieldValue(
        "invoicePeriodFrom",
        selectedCourse.invoicePeriodFrom || "",
      );
      formik.setFieldValue(
        "invoicePeriodTo",
        selectedCourse.invoicePeriodTo || "",
      );
      formik.setFieldValue("dueDate", selectedCourse.dueDate || "");

      const currentInvoiceItems = [...formik.values.invoiceItems];
      const courseFeeIndex = currentInvoiceItems.findIndex(
        (item) =>
          item.item !== "Registration Fee" &&
          item.item !== "Deposit Fee" &&
          !(typeof item.item === "string" && item.item.startsWith("CN-REF")),
      );

      if (courseFeeIndex !== -1) {
        currentInvoiceItems[courseFeeIndex] = {
          item: selectedCourse.courseFeeName || "Course Fee",
          itemAmount: 0,
          taxType: selectedCourse.courseTaxId
            ? selectedCourse.courseTaxId.toString()
            : "",
          gstAmount: 0,
          totalAmount: parseFloat(selectedCourse.courseFee || 0),
        };

        if (
          currentInvoiceItems[courseFeeIndex].taxType &&
          parseFloat(currentInvoiceItems[courseFeeIndex].totalAmount)
        ) {
          const tax = taxData.find(
            (t) =>
              t.id === parseInt(currentInvoiceItems[courseFeeIndex].taxType),
          );
          const totalAmount = parseFloat(
            currentInvoiceItems[courseFeeIndex].totalAmount || 0,
          );
          if (tax && totalAmount !== 0) {
            const itemAmount = Math.round(totalAmount / (1 + tax.rate / 100));
            const gstAmount = Math.round(totalAmount - itemAmount);
            currentInvoiceItems[courseFeeIndex].itemAmount = itemAmount;
            currentInvoiceItems[courseFeeIndex].gstAmount = gstAmount;
          } else {
            currentInvoiceItems[courseFeeIndex].itemAmount = totalAmount;
            currentInvoiceItems[courseFeeIndex].gstAmount = 0;
          }
        } else {
          currentInvoiceItems[courseFeeIndex].itemAmount =
            currentInvoiceItems[courseFeeIndex].totalAmount || 0;
          currentInvoiceItems[courseFeeIndex].gstAmount = 0;
        }

        formik.setFieldValue("invoiceItems", currentInvoiceItems);
        console.log(
          "Updated invoiceItems (course change):",
          currentInvoiceItems,
        );
      }

      const totalGST = currentInvoiceItems.reduce(
        (sum, item) => sum + parseFloat(item.gstAmount || 0),
        0,
      );
      const totalAmountSum = currentInvoiceItems.reduce(
        (sum, item) =>
          sum +
          (typeof item.item === "string" && item.item.startsWith("CN-REF")
            ? 0
            : parseFloat(item.totalAmount || 0)),
        0,
      );
      formik.setFieldValue("gst", totalGST.toString());
      // formik.setFieldValue(
      //   "totalAmount",
      //   (
      //     totalAmountSum - parseFloat(formik.values.creditAdviceOffset || 0)
      //   ).toString()
      // );
      formik.setFieldValue("totalAmount", totalAmountSum.toString());
    }
  };

  // Handle package change
  const handlePackageChange = async (packageId) => {
    if (!packageId || !formik.values.student || !formik.values.course) return;

    const classId = await fetchClassSchedules(
      formik.values.student,
      formik.values.course,
    );

    const invoiceData = await fetchInvoiceInfo(
      formik.values.student,
      formik.values.course,
      packageId,
      classId,
    );

    if (invoiceData) {
      const selectedCourse = invoiceData.courses[0] || {};

      // Update main fields
      formik.setFieldValue("noOfLessons", selectedCourse.noOfLessons || "");
      formik.setFieldValue("perLessonCost", selectedCourse.perLessonCost || "");
      formik.setFieldValue(
        "invoicePeriodFrom",
        selectedCourse.invoicePeriodFrom || "",
      );
      formik.setFieldValue(
        "invoicePeriodTo",
        selectedCourse.invoicePeriodTo || "",
      );
      formik.setFieldValue("dueDate", selectedCourse.dueDate || "");

      const currentInvoiceItems = [...formik.values.invoiceItems];

      // ------------------ COURSE FEE UPDATE ------------------
      const courseFeeIndex = currentInvoiceItems.findIndex(
        (item) =>
          item.item !== "Registration Fee" &&
          item.item !== "Deposit Fee" &&
          !(typeof item.item === "string" && item.item.startsWith("CN-REF")) &&
          !(
            typeof item.item === "string" &&
            item.item.toLowerCase().includes("material")
          ),
      );

      if (courseFeeIndex !== -1) {
        currentInvoiceItems[courseFeeIndex] = {
          item: selectedCourse.courseFeeName || "Course Fee",
          itemAmount: 0,
          taxType: selectedCourse.courseTaxId
            ? selectedCourse.courseTaxId.toString()
            : "",
          gstAmount: 0,
          totalAmount: parseFloat(selectedCourse.courseFee || 0),
        };
      }

      // ------------------ MATERIALS FEE UPDATE ------------------
      const hasMaterialsFee = parseFloat(selectedCourse.materialsFee || 0) > 0;

      const materialsIndex = currentInvoiceItems.findIndex(
        (item) =>
          typeof item.item === "string" &&
          item.item.toLowerCase().includes("material"),
      );

      if (hasMaterialsFee) {
        let materialTotal = parseFloat(selectedCourse.materialsFee || 0);
        let materialItemAmount = materialTotal;
        let materialGST = 0;

        if (selectedCourse.materialsTaxId) {
          const tax = taxData.find(
            (t) => t.id === Number(selectedCourse.materialsTaxId),
          );
          if (tax) {
            materialItemAmount = Math.round(
              materialTotal / (1 + tax.rate / 100),
            );
            materialGST = Math.round(materialTotal - materialItemAmount);
          }
        }

        const materialRow = {
          item: formatMaterialsLabel(selectedCourse.noOfLessons),
          itemAmount: materialItemAmount,
          taxType: selectedCourse.materialsTaxId
            ? selectedCourse.materialsTaxId.toString()
            : "",
          gstAmount: materialGST,
          totalAmount: materialTotal,
        };

        if (materialsIndex !== -1) {
          currentInvoiceItems[materialsIndex] = materialRow;
        } else {
          currentInvoiceItems.splice(courseFeeIndex + 1, 0, materialRow);
        }
      } else {
        if (materialsIndex !== -1) {
          currentInvoiceItems.splice(materialsIndex, 1);
        }
      }

      // ------------------ GST CALCULATION ------------------
      currentInvoiceItems.forEach((item, index) => {
        if (item.taxType && parseFloat(item.totalAmount)) {
          const tax = taxData.find((t) => t.id === parseInt(item.taxType));
          const totalAmount = parseFloat(item.totalAmount || 0);
          if (tax && totalAmount !== 0) {
            const itemAmount = Math.round(totalAmount / (1 + tax.rate / 100));
            const gstAmount = Math.round(totalAmount - itemAmount);
            currentInvoiceItems[index].itemAmount = itemAmount;
            currentInvoiceItems[index].gstAmount = gstAmount;
          } else {
            currentInvoiceItems[index].itemAmount = totalAmount;
            currentInvoiceItems[index].gstAmount = 0;
          }
        } else {
          currentInvoiceItems[index].itemAmount = item.totalAmount || 0;
          currentInvoiceItems[index].gstAmount = 0;
        }
      });

      // ------------------ UPDATE FORM ------------------
      formik.setFieldValue("invoiceItems", currentInvoiceItems);

      const totalGST = currentInvoiceItems.reduce(
        (sum, item) => sum + parseFloat(item.gstAmount || 0),
        0,
      );

      const totalAmountSum = currentInvoiceItems.reduce(
        (sum, item) =>
          sum +
          (typeof item.item === "string" && item.item.startsWith("CN-REF")
            ? 0
            : parseFloat(item.totalAmount || 0)),
        0,
      );

      formik.setFieldValue("gst", totalGST.toString());
      formik.setFieldValue("totalAmount", totalAmountSum.toString());
    } else {
      const selectedPackage = centerPackageOptions.find(
        (pkg) => pkg.value === packageId,
      );
      if (selectedPackage) {
        formik.setFieldValue("noOfLessons", selectedPackage.noOfLessons || "");
      }
    }
  };

  // Handle tax type change
  const handleSelectChange = (index, taxId) => {
    console.log(`Tax type changed for index ${index}:`, taxId);
    formik.setFieldValue(`invoiceItems[${index}].taxType`, taxId);
    const totalAmount = parseFloat(
      formik.values.invoiceItems[index].totalAmount || 0,
    );

    if (taxId && totalAmount !== 0) {
      const tax = taxData.find((t) => t.id === parseInt(taxId));
      let itemAmount = totalAmount;
      let gstAmount = 0;
      if (tax) {
        itemAmount = Math.round(totalAmount / (1 + tax.rate / 100));
        gstAmount = Math.round(totalAmount - itemAmount);
      }
      formik.setFieldValue(
        `invoiceItems[${index}].itemAmount`,
        itemAmount.toString(),
      );
      formik.setFieldValue(
        `invoiceItems[${index}].gstAmount`,
        gstAmount.toString(),
      );
    } else {
      formik.setFieldValue(
        `invoiceItems[${index}].itemAmount`,
        totalAmount.toString(),
      );
      formik.setFieldValue(`invoiceItems[${index}].gstAmount`, "0");
    }

    const totalGST = formik.values.invoiceItems.reduce(
      (sum, item) => sum + parseFloat(item.gstAmount || 0),
      0,
    );
    const totalAmountSum = formik.values.invoiceItems.reduce(
      (sum, item) =>
        sum +
        (typeof item.item === "string" && item.item.startsWith("CN-REF")
          ? 0
          : parseFloat(item.totalAmount || 0)),
      0,
    );
    formik.setFieldValue("gst", totalGST.toString());
    // formik.setFieldValue(
    //   "totalAmount",
    //   (
    //     totalAmountSum - parseFloat(formik.values.creditAdviceOffset || 0)
    //   ).toString()
    // );
    formik.setFieldValue("totalAmount", totalAmountSum.toString());
  };

  // const handleTotalAmountChange = (index, value) => {
  //   let amt = parseFloat(value || 0);

  //   const row = formik.values.invoiceItems[index];
  //   const isCredit =
  //     typeof row.item === "string" && row.item.startsWith("CN-REF");

  //   // If row is Credit Note → always negative & no GST
  //   if (isCredit) {
  //     amt = -Math.abs(amt); // force negative
  //     formik.setFieldValue(
  //       `invoiceItems[${index}].totalAmount`,
  //       amt.toString(),
  //     );
  //     formik.setFieldValue(`invoiceItems[${index}].itemAmount`, amt.toString());
  //     formik.setFieldValue(`invoiceItems[${index}].gstAmount`, "0");
  //   } else {
  //     // normal item calc
  //     formik.setFieldValue(
  //       `invoiceItems[${index}].totalAmount`,
  //       amt.toString(),
  //     );
  //     const taxId = row.taxType;
  //     if (taxId && amt !== 0) {
  //       const tax = taxData.find((t) => t.id === parseInt(taxId));
  //       if (tax) {
  //         const itemAmount = Math.round(amt / (1 + tax.rate / 100));
  //         const gstAmount = Math.round(amt - itemAmount);
  //         formik.setFieldValue(
  //           `invoiceItems[${index}].itemAmount`,
  //           itemAmount.toString(),
  //         );
  //         formik.setFieldValue(
  //           `invoiceItems[${index}].gstAmount`,
  //           gstAmount.toString(),
  //         );
  //       }
  //     } else {
  //       formik.setFieldValue(
  //         `invoiceItems[${index}].itemAmount`,
  //         amt.toString(),
  //       );
  //       formik.setFieldValue(`invoiceItems[${index}].gstAmount`, "0");
  //     }
  //   }

  //   // Recalculate GST
  //   const totalGST = formik.values.invoiceItems.reduce(
  //     (sum, item) => sum + parseFloat(item.gstAmount || 0),
  //     0,
  //   );

  //   // Recalculate final amount — INCLUDE negative credit values
  //   const totalAmountSum = formik.values.invoiceItems.reduce(
  //     (sum, item) => sum + parseFloat(item.totalAmount || 0),
  //     0,
  //   );

  //   formik.setFieldValue("gst", totalGST.toString());
  //   // formik.setFieldValue(
  //   //   "totalAmount",
  //   //   (
  //   //     totalAmountSum - parseFloat(formik.values.creditAdviceOffset || 0)
  //   //   ).toString()
  //   // );
  //   formik.setFieldValue("totalAmount", totalAmountSum.toString());
  // };

  // Handle row deletion
  
  const handleTotalAmountChange = (index, value) => {
  let amt = parseFloat(value || 0);

  const updatedItems = [...formik.values.invoiceItems];

  const row = updatedItems[index];

  const isCredit =
    typeof row.item === "string" && row.item.startsWith("CN-REF");

  if (isCredit) {
    amt = -Math.abs(amt);

    updatedItems[index] = {
      ...row,
      totalAmount: amt,
      itemAmount: amt,
      gstAmount: 0,
    };
  } else {
    const taxId = row.taxType;

    let itemAmount = amt;
    let gstAmount = 0;

    if (taxId && amt !== 0) {
      const tax = taxData.find((t) => t.id === parseInt(taxId));

      if (tax) {
        itemAmount = Math.round(amt / (1 + tax.rate / 100));
        gstAmount = Math.round(amt - itemAmount);
      }
    }

    updatedItems[index] = {
      ...row,
      totalAmount: amt,
      itemAmount,
      gstAmount,
    };
  }

  // ✅ Update full array first
  formik.setFieldValue("invoiceItems", updatedItems);

  // ✅ Correct GST Total
  const totalGST = updatedItems.reduce(
    (sum, item) => sum + Number(item.gstAmount || 0),
    0
  );

  // ✅ Correct Final Total
  const totalAmountSum = updatedItems.reduce(
    (sum, item) => sum + Number(item.totalAmount || 0),
    0
  );

  formik.setFieldValue("gst", totalGST);
  formik.setFieldValue("totalAmount", totalAmountSum);
};

  const handleRowDelete = (index) => {
    const courseFeeName =
      studentOverallData?.courses?.[0]?.courseFeeName || "Course Fee";
    if (
      formik.values.invoiceItems[index]?.item !== "Registration Fee" &&
      formik.values.invoiceItems[index]?.item !== courseFeeName &&
      formik.values.invoiceItems[index]?.item !== "Deposit Fee" &&
      !(
        typeof formik.values.invoiceItems[index]?.item === "string" &&
        formik.values.invoiceItems[index]?.item.startsWith("CN-REF")
      )
    ) {
      const newInvoiceItems = formik.values.invoiceItems.filter(
        (_, i) => i !== index,
      );
      formik.setFieldValue("invoiceItems", newInvoiceItems);
      console.log("Deleted row, new invoiceItems:", newInvoiceItems);

      const totalGST = newInvoiceItems.reduce(
        (sum, item) => sum + parseFloat(item.gstAmount || 0),
        0,
      );
      const totalAmountSum = newInvoiceItems.reduce(
        (sum, item) => sum + parseFloat(item.totalAmount || 0),
        0,
      );

      formik.setFieldValue("gst", totalGST.toString());
      // formik.setFieldValue(
      //   "totalAmount",
      //   (
      //     totalAmountSum - parseFloat(formik.values.creditAdviceOffset || 0)
      //   ).toString()
      // );
      formik.setFieldValue("totalAmount", totalAmountSum.toString());
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchTaxData();
    // fetchStudent();
    fetchPackagesByCenter();
  }, [selectedCenter]);

  useEffect(() => {
    if (taxData.length === 0) return;
    if (!formik.values.invoiceItems?.length) return;

    recalcInvoiceGST(formik.values.invoiceItems);
  }, [taxData]);

  // 🔹 Auto-select student when studentID + options are ready
  useEffect(() => {
    if (!studentID) return;
    if (studentOptions.length === 0) return;

    const matchedStudent = studentOptions.find(
      (opt) => opt.value === Number(studentID),
    );

    if (matchedStudent) {
      formik.setFieldValue("student", matchedStudent.value);
      handleStudentChange(matchedStudent.value);
    }
  }, [studentID, studentOptions]);

  // Scroll to first error on submit
  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      const errorField = Object.keys(formik.errors)[0];
      const errorElement =
        document.querySelector(`[name="${errorField}"]`) ||
        document.querySelector(`[name="invoiceItems[0].${errorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus();
      }
    }
  }, [formik.submitCount, formik.errors]);

  useEffect(() => {
    if (!selectedCenter || selectedCenter === "0") return;

    // reset student + related data
    setStudentOptions([]);
    formik.setFieldValue("student", "");
    resetFormFields();

    fetchStudent();
    fetchPackagesByCenter();
  }, [selectedCenter]);

  return (
    <div className="container-fluid">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Invoice and Payment
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          <Link to="/invoice" className="custom-breadcrumb">
            Invoice
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="active breadcrumb-item" aria-current="page">
          Invoice Add
        </li>
      </ol>
      <form
        onSubmit={formik.handleSubmit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !formik.isSubmitting) {
            e.preventDefault();
          }
        }}
      >
        <div className="card">
          <div
            className="d-flex align-items-center justify-content-between p-1 mb-4 px-4"
            style={{ background: "#f5f7f9" }}
          >
            <div className="d-flex align-items-center">
              <div className="d-flex">
                <div className="active dot"></div>
              </div>
              <span className="text-muted me-2">Add Invoice</span>
            </div>
            <div className="d-flex align-items-center my-2">
              <Link to="/invoice">
                <button type="button" className="btn btn-border btn-sm mx-2">
                  Back
                </button>
              </Link>
              <button
                type="submit"
                className="btn btn-button btn-sm"
                disabled={loadIndicator}
              >
                {loadIndicator && (
                  <span
                    className="me-2 spinner-border spinner-border-sm"
                    aria-hidden="true"
                  ></span>
                )}
                Generate
              </button>
            </div>
          </div>
          <div className="container-fluid py-3">
            <div className="row mt-3">
              <div className="col-12 col-lg-6 col-md-6 px-5">
                <div className="text-start mt-3">
                  <label htmlFor="student" className="fw-medium mb-1">
                    Student<span className="text-danger">*</span>
                  </label>
                  <br />
                  <Select
                    options={studentOptions}
                    name="student"
                    value={studentOptions.find(
                      (option) => option.value === formik.values.student,
                    )}
                    onChange={(selectedOption) => {
                      console.log("Student select changed:", selectedOption);
                      const studentId = selectedOption
                        ? selectedOption.value
                        : "";
                      formik.setFieldValue("student", studentId);
                      if (studentId) {
                        handleStudentChange(studentId);
                      } else {
                        resetFormFields();
                      }
                    }}
                    placeholder="Select Student"
                    isSearchable
                    isClearable
                    className={`${
                      formik.touched.student && formik.errors.student
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.student && formik.errors.student && (
                    <div className="invalid-feedback">
                      {formik.errors.student}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="parent" className="fw-medium mb-1">
                    Parent<span className="text-danger">*</span>
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("parent")}
                    className={`form-control ${
                      formik.touched.parent && formik.errors.parent
                        ? "is-invalid"
                        : ""
                    }`}
                    type="text"
                    readOnly
                    autoComplete="off"
                  />
                  {formik.touched.parent && formik.errors.parent && (
                    <div className="invalid-feedback">
                      {formik.errors.parent}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="course" className="fw-medium mb-1">
                    Course<span className="text-danger">*</span>
                  </label>
                  <br />
                  <Select
                    options={courseOptions}
                    name="course"
                    value={courseOptions.find(
                      (option) => option.value === formik.values.course,
                    )}
                    onChange={(selectedOption) => {
                      console.log("Course select changed:", selectedOption);
                      const courseId = selectedOption
                        ? selectedOption.value
                        : "";
                      formik.setFieldValue("course", courseId);
                      if (courseId) {
                        handleCourseChange(courseId);
                      }
                    }}
                    placeholder="Select Course"
                    isSearchable
                    isClearable
                    className={`${
                      formik.touched.course && formik.errors.course
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.course && formik.errors.course && (
                    <div className="invalid-feedback">
                      {formik.errors.course}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="schedule" className="fw-medium mb-1">
                    Schedule
                  </label>
                  <br />
                  <Select
                    options={scheduleOptions}
                    name="schedule"
                    value={scheduleOptions.find(
                      (option) => option.value === formik.values.schedule,
                    )}
                    // onChange={async (selectedOption) => {
                    //   const scheduleValue = selectedOption
                    //     ? selectedOption.value
                    //     : "";
                    //   const classId = selectedOption
                    //     ? selectedOption.classId
                    //     : "";

                    //   // update Formik field
                    //   formik.setFieldValue("schedule", scheduleValue);
                    //   formik.setFieldValue("classId", classId);
                    //   // Re-call Invoice API with exact classId
                    //   if (
                    //     formik.values.student &&
                    //     formik.values.course &&
                    //     formik.values.packageId
                    //   ) {
                    //     await fetchInvoiceInfo(
                    //       formik.values.student,
                    //       formik.values.course,
                    //       formik.values.packageId,
                    //       classId, // ← Send classId to API
                    //     );
                    //   }
                    // }}
                    onChange={async (selectedOption) => {
                      const scheduleValue = selectedOption?.value || "";
                      const classId = selectedOption?.classId || "";

                      formik.setFieldValue("schedule", scheduleValue);
                      formik.setFieldValue("classId", classId);

                      if (
                        formik.values.student &&
                        formik.values.course &&
                        formik.values.packageId &&
                        classId
                      ) {
                        const invoiceData = await fetchInvoiceInfo(
                          formik.values.student,
                          formik.values.course,
                          formik.values.packageId,
                          classId,
                        );

                        // 🔥 THIS IS THE KEY LINE
                        applyInvoiceDataToForm(invoiceData);
                      }
                    }}
                    placeholder="Select a schedule"
                    isSearchable
                    isClearable
                  />

                  {formik.touched.schedule && formik.errors.schedule && (
                    <div className="invalid-feedback">
                      {formik.errors.schedule}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="packageId" className="fw-medium mb-1">
                    Package<span className="text-danger">*</span>
                  </label>
                  <br />
                  <Select
                    options={packageOptions}
                    name="packageId"
                    value={packageOptions.find(
                      (option) => option.value === formik.values.packageId,
                    )}
                    onChange={(selectedOption) => {
                      console.log("Package select changed:", selectedOption);
                      const packageId = selectedOption
                        ? selectedOption.value
                        : "";
                      formik.setFieldValue("packageId", packageId);
                      if (packageId) {
                        handlePackageChange(packageId);
                      }
                    }}
                    placeholder="Select Package"
                    isSearchable
                    isClearable
                    className={`${
                      formik.touched.packageId && formik.errors.packageId
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.packageId && formik.errors.packageId && (
                    <div className="invalid-feedback">
                      {formik.errors.packageId}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="perLessonCost" className="fw-medium mb-1">
                    Per-Lesson Cost
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("perLessonCost")}
                    className="form-control"
                    type="text"
                    readOnly
                  />
                </div>
              </div>
              <div className="col-12 col-lg-6 col-md-6 px-5">
                <div className="text-start mt-3">
                  <label htmlFor="invoiceDate" className="fw-medium mb-1">
                    Invoice Date<span className="text-danger">*</span>
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("invoiceDate")}
                    className={`form-control ${
                      formik.touched.invoiceDate && formik.errors.invoiceDate
                        ? "is-invalid"
                        : ""
                    }`}
                    type="date"
                    autoComplete="off"
                    onChange={(e) => {
                      console.log("Invoice date changed:", e.target.value);
                      const invoiceDate = e.target.value;
                      formik.setFieldValue("invoiceDate", invoiceDate);
                      if (!invoiceDate) {
                        formik.setFieldValue("dueDate", "");
                      } else {
                        const newDueDate =
                          formik.values.invoicePeriodTo ||
                          new Date(
                            new Date(invoiceDate).setMonth(
                              new Date(invoiceDate).getMonth() + 1,
                            ),
                          )
                            .toISOString()
                            .split("T")[0];
                        formik.setFieldValue("dueDate", newDueDate);
                      }
                    }}
                  />
                  {formik.touched.invoiceDate && formik.errors.invoiceDate && (
                    <div className="invalid-feedback">
                      {formik.errors.invoiceDate}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="dueDate" className="fw-medium mb-1">
                    Due Date<span className="text-danger">*</span>
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("dueDate")}
                    className={`form-control ${
                      formik.touched.dueDate && formik.errors.dueDate
                        ? "is-invalid"
                        : ""
                    }`}
                    type="date"
                    autoComplete="off"
                    onChange={(e) => {
                      console.log("Due date changed:", e.target.value);
                      formik.setFieldValue("dueDate", e.target.value);
                    }}
                  />
                  {formik.touched.dueDate && formik.errors.dueDate && (
                    <div className="invalid-feedback">
                      {formik.errors.dueDate}
                    </div>
                  )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="invoicePeriodFrom" className="fw-medium mb-1">
                    Invoice Period From<span className="text-danger">*</span>
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("invoicePeriodFrom")}
                    className={`form-control ${
                      formik.touched.invoicePeriodFrom &&
                      formik.errors.invoicePeriodFrom
                        ? "is-invalid"
                        : ""
                    }`}
                    type="date"
                    autoComplete="off"
                  />
                  {formik.touched.invoicePeriodFrom &&
                    formik.errors.invoicePeriodFrom && (
                      <div className="invalid-feedback">
                        {formik.errors.invoicePeriodFrom}
                      </div>
                    )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="invoicePeriodTo" className="fw-medium mb-1">
                    Invoice Period To<span className="text-danger">*</span>
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("invoicePeriodTo")}
                    className={`form-control ${
                      formik.touched.invoicePeriodTo &&
                      formik.errors.invoicePeriodTo
                        ? "is-invalid"
                        : ""
                    }`}
                    type="date"
                    autoComplete="off"
                    onChange={(e) => {
                      console.log("Invoice period to changed:", e.target.value);
                      formik.setFieldValue("invoicePeriodTo", e.target.value);
                      if (e.target.value) {
                        formik.setFieldValue("dueDate", e.target.value);
                      }
                    }}
                  />
                  {formik.touched.invoicePeriodTo &&
                    formik.errors.invoicePeriodTo && (
                      <div className="invalid-feedback">
                        {formik.errors.invoicePeriodTo}
                      </div>
                    )}
                </div>
                <div className="text-start mt-3">
                  <label htmlFor="noOfLessons" className="fw-medium mb-1">
                    Number of Lessons
                  </label>
                  <br />
                  <input
                    {...formik.getFieldProps("noOfLessons")}
                    className="form-control"
                    type="text"
                    readOnly
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
            <div className="flex-nowrap row mt-5 pt-5">
              <div className="col-12">
                <div className="table-responsive">
                  <table
                    className="table table-light table-bordered table-nowrap"
                    style={{ borderCollapse: "collapse" }}
                  >
                    <thead style={{ backgroundColor: "#f5f5f5" }}>
                      <tr>
                        <th
                          style={{
                            width: "27%",
                            padding: "10px",
                            fontWeight: "bold",
                          }}
                        >
                          Item<span className="text-danger">*</span>
                        </th>
                        <th
                          style={{
                            width: "12%",
                            padding: "10px",
                            fontWeight: "bold",
                            textAlign: "left",
                          }}
                        >
                          Item Amount (Exc GST)
                          <span className="text-danger">*</span>
                        </th>
                        <th
                          style={{
                            width: "20%",
                            padding: "10px",
                            fontWeight: "bold",
                            textAlign: "left",
                          }}
                        >
                          Tax Type<span className="text-danger">*</span>
                        </th>
                        <th
                          style={{
                            width: "8%",
                            padding: "10px",
                            fontWeight: "bold",
                            textAlign: "left",
                          }}
                        >
                          GST Amount<span className="text-danger">*</span>
                        </th>
                        <th
                          style={{
                            width: "10%",
                            padding: "10px",
                            fontWeight: "bold",
                            textAlign: "left",
                          }}
                        >
                          Total Amount (Inc GST)
                          <span className="text-danger">*</span>
                        </th>
                        <th style={{ width: "5%", padding: "10px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formik.values.invoiceItems.map((item, index) => {
                        const courseFeeName =
                          studentOverallData?.courses?.[0]?.courseFeeName ||
                          "Course Fee";
                        // const isMaterialsFee =
                        //   typeof item.item === "string" &&
                        //   item.item.toLowerCase().startsWith("materials fee");
                        const isNonDeletable =
                          item.item === "Registration Fee" ||
                          item.item === courseFeeName ||
                          item.item === "Deposit Fee" ||
                          // isMaterialsFee ||
                          (typeof item.item === "string" &&
                            item.item.startsWith("CN-REF"));

                        return (
                          <tr key={index}>
                            <td style={{ padding: "8px" }}>
                              <textarea
                                rows={2}
                                {...formik.getFieldProps(
                                  `invoiceItems[${index}].item`,
                                )}
                                className={`form-control ${
                                  formik.touched.invoiceItems?.[index]?.item &&
                                  formik.errors.invoiceItems?.[index]?.item
                                    ? "is-invalid"
                                    : ""
                                }`}
                                type="text"
                                style={{ width: "100%", padding: "6px" }}
                                autoComplete="off"
                                onChange={(e) => {
                                  console.log(
                                    `Item changed for index ${index}:`,
                                    e.target.value,
                                  );
                                  formik.setFieldValue(
                                    `invoiceItems[${index}].item`,
                                    e.target.value,
                                  );
                                }}
                                // readOnly={isMaterialsFee}
                                readOnly={false}
                              />
                              {formik.touched.invoiceItems?.[index]?.item &&
                                formik.errors.invoiceItems?.[index]?.item && (
                                  <div className="invalid-feedback">
                                    {formik.errors.invoiceItems[index].item}
                                  </div>
                                )}
                            </td>
                            <td style={{ padding: "8px", textAlign: "right" }}>
                              <input
                                {...formik.getFieldProps(
                                  `invoiceItems[${index}].itemAmount`,
                                )}
                                className={`form-control ${
                                  formik.touched.invoiceItems?.[index]
                                    ?.itemAmount &&
                                  formik.errors.invoiceItems?.[index]
                                    ?.itemAmount
                                    ? "is-invalid"
                                    : ""
                                }`}
                                type="number"
                                style={{ padding: "6px", textAlign: "right" }}
                                readOnly
                                autoComplete="off"
                              />
                              {formik.touched.invoiceItems?.[index]
                                ?.itemAmount &&
                                formik.errors.invoiceItems?.[index]
                                  ?.itemAmount && (
                                  <div className="invalid-feedback">
                                    {
                                      formik.errors.invoiceItems[index]
                                        .itemAmount
                                    }
                                  </div>
                                )}
                            </td>
                            <td style={{ padding: "8px" }}>
                              <select
                                className={`form-select ${
                                  formik.touched.invoiceItems?.[index]
                                    ?.taxType &&
                                  formik.errors.invoiceItems?.[index]?.taxType
                                    ? "is-invalid"
                                    : ""
                                }`}
                                {...formik.getFieldProps(
                                  `invoiceItems[${index}].taxType`,
                                )}
                                style={{ padding: "6px" }}
                                onChange={(e) =>
                                  handleSelectChange(index, e.target.value)
                                }
                                // disabled={isMaterialsFee}
                                disabled={false}
                              >
                                <option value="">Select Tax Type</option>
                                {taxData &&
                                  taxData.map((tax) => (
                                    <option key={tax.id} value={tax.id}>
                                      {`${tax.taxType} ${tax.rate}%`}
                                    </option>
                                  ))}
                              </select>
                              {formik.touched.invoiceItems?.[index]?.taxType &&
                                formik.errors.invoiceItems?.[index]
                                  ?.taxType && (
                                  <div className="invalid-feedback">
                                    {formik.errors.invoiceItems[index].taxType}
                                  </div>
                                )}
                            </td>
                            <td style={{ padding: "8px", textAlign: "right" }}>
                              <input
                                {...formik.getFieldProps(
                                  `invoiceItems[${index}].gstAmount`,
                                )}
                                className={`form-control ${
                                  formik.touched.invoiceItems?.[index]
                                    ?.gstAmount &&
                                  formik.errors.invoiceItems?.[index]?.gstAmount
                                    ? "is-invalid"
                                    : ""
                                }`}
                                type="number"
                                style={{
                                  width: "100%",
                                  padding: "6px",
                                  textAlign: "right",
                                }}
                                readOnly
                                autoComplete="off"
                              />
                              {formik.touched.invoiceItems?.[index]
                                ?.gstAmount &&
                                formik.errors.invoiceItems?.[index]
                                  ?.gstAmount && (
                                  <div className="invalid-feedback">
                                    {
                                      formik.errors.invoiceItems[index]
                                        .gstAmount
                                    }
                                  </div>
                                )}
                            </td>
                            <td style={{ padding: "8px", textAlign: "right" }}>
                              <input
                                name={`invoiceItems[${index}].totalAmount`}
                                value={
                                  formik.values.invoiceItems[index]
                                    ?.totalAmount || ""
                                }
                                onChange={(e) =>
                                  handleTotalAmountChange(index, e.target.value)
                                }
                                onBlur={formik.handleBlur}
                                className={`form-control ${
                                  formik.touched.invoiceItems?.[index]
                                    ?.totalAmount &&
                                  formik.errors.invoiceItems?.[index]
                                    ?.totalAmount
                                    ? "is-invalid"
                                    : ""
                                }`}
                                type="number"
                                style={{
                                  width: "100%",
                                  padding: "6px",
                                  textAlign: "right",
                                }}
                                autoComplete="off"
                                // readOnly={
                                //   isMaterialsFee ||
                                //   (typeof item.item === "string" &&
                                //     item.item.startsWith("CN-REF"))
                                // }
                                readOnly={
                                  typeof item.item === "string" &&
                                  item.item.startsWith("CN-REF")
                                }
                              />
                              {formik.touched.invoiceItems?.[index]
                                ?.totalAmount &&
                                formik.errors.invoiceItems?.[index]
                                  ?.totalAmount && (
                                  <div className="invalid-feedback">
                                    {
                                      formik.errors.invoiceItems[index]
                                        .totalAmount
                                    }
                                  </div>
                                )}
                            </td>
                            <td style={{ padding: "8px", textAlign: "center" }}>
                              {!isNonDeletable && (
                                <button
                                  type="button"
                                  className="btn btn-white border-white"
                                  onClick={() => handleRowDelete(index)}
                                >
                                  <ImCancelCircle className="text-danger fs-6 fw-medium" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12 text-end mt-3">
                <button
                  type="button"
                  className="btn btn-sm text-white"
                  style={{
                    fontWeight: "600px !important",
                    background: "#eb862a",
                  }}
                  onClick={() => {
                    const newInvoiceItems = [
                      ...formik.values.invoiceItems,
                      {
                        item: "",
                        itemAmount: 0,
                        taxType: "",
                        gstAmount: 0,
                        totalAmount: 0,
                      },
                    ];
                    formik.setFieldValue("invoiceItems", newInvoiceItems);
                    console.log(
                      "Added new row, invoiceItems:",
                      newInvoiceItems,
                    );
                  }}
                >
                  Add Row
                </button>
              </div>
              <div className="col-12 col-lg-6 col-md-6"></div>
              <div className="col-12 col-lg-6 col-md-6">
                <div className="">
                  <div className="text-start mt-3">
                    <label
                      htmlFor="creditAdviceOffset"
                      className="fw-medium mb-1"
                    >
                      Credit Advice Offset
                    </label>
                    <br />
                    <input
                      {...formik.getFieldProps("creditAdviceOffset")}
                      className="form-control"
                      type="text"
                      readOnly
                      autoComplete="off"
                    />
                  </div>
                  <div className="text-start mt-3">
                    <label htmlFor="gst" className="fw-medium mb-1">
                      GST Amount
                    </label>
                    <br />
                    <input
                      {...formik.getFieldProps("gst")}
                      className="form-control"
                      type="text"
                      readOnly
                      autoComplete="off"
                    />
                  </div>
                  <div className="text-start mt-3">
                    <label htmlFor="totalAmount" className="fw-medium mb-1">
                      Total Amount
                    </label>
                    <br />
                    <input
                      {...formik.getFieldProps("totalAmount")}
                      className="form-control"
                      type="text"
                      readOnly
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default GenerateInvoiceAdd;
